import { z } from "zod";
import {
  FREIGHT_MODES,
  MAX_IPI_PCT4,
  MAX_ITEM_DISCOUNT_PCT4,
  MAX_PRICE_CENTS,
  MAX_PURCHASE_ORDER_ITEMS,
  MAX_STOCK_QTY_Q4,
  isIsoDate,
  type BaseState,
  type FreightMode,
  type StatusTone,
} from "@cx/domain";
import { listQuery } from "./common";
import { blank, text } from "./fields";
import { ulidParam } from "./ids";
import { lotQtyInput } from "./lots";
import { businessDate, type InstallmentView } from "./prices";
import type { StatusView } from "./statuses";

// ===== Pedidos de compra e recebimento parcial (T2.6, RF-SUP-03) =====

const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);
const cents = (what: string) =>
  z
    .number(`Informe ${what}`)
    .int("Use centavos inteiros")
    .min(0, "Valor não pode ser negativo")
    .max(MAX_PRICE_CENTS, "Valor acima do limite");
const optDate = blank.pipe(businessDate.nullable());
const qtyQ4 = z
  .number("Informe a quantidade")
  .int("Quantidade inválida")
  .min(1, "A quantidade deve ser maior que zero")
  .max(MAX_STOCK_QTY_Q4, "Quantidade acima do limite");

/**
 * Item: produto cadastrado (simples ou variação; kit e pai de grade são recusados). `unitPriceCents` nulo = último
 * custo do fornecedor ou preço de custo do produto (só com products:read_cost; sem ela o preço é obrigatório).
 */
export const purchaseOrderItemInput = z.object({
  productId: ulidParam,
  qtyQ4,
  unitPriceCents: nullish(cents("o preço")),
  ipiPct4: z
    .number("Informe o IPI")
    .int("Percentual inválido")
    .min(0, "O IPI vai de 0% a 100%")
    .max(MAX_IPI_PCT4, "O IPI vai de 0% a 100%")
    .default(0),
});
export type PurchaseOrderItemInput = z.output<typeof purchaseOrderItemInput>;

export const purchaseOrderInput = z
  .object({
    supplierId: ulidParam,
    date: businessDate,
    expectedDate: optDate,
    /** "Ordem de compra" (referência do comprador). */
    purchaseRef: text(60),
    /** Depósito de entrada padrão dos recebimentos; nulo = padrão da empresa. */
    warehouseId: nullish(ulidParam),
    items: z
      .array(purchaseOrderItemInput, "Informe os itens")
      .min(1, "Inclua ao menos um item")
      .max(MAX_PURCHASE_ORDER_ITEMS, `No máximo ${MAX_PURCHASE_ORDER_ITEMS} itens`),
    /** Desconto geral em % sobre os produtos; null = em valor (`discountCents`). */
    discountPct4: nullish(
      z
        .number("Informe o percentual")
        .int("Percentual inválido")
        .min(0, "O desconto vai de 0% a 100%")
        .max(MAX_ITEM_DISCOUNT_PCT4, "O desconto vai de 0% a 100%"),
    ),
    discountCents: cents("o desconto").default(0),
    freightCents: cents("o frete").default(0),
    otherExpensesCents: cents("as outras despesas").default(0),
    icmsStCents: cents("o ICMS-ST").default(0),
    /** Condição de pagamento: parcelas previstas e as contas de cada recebimento (05 §4). */
    paymentTermId: nullish(ulidParam),
    /** Categoria das contas a pagar; nula = "Compras de Mercadorias". */
    financialCategoryId: nullish(ulidParam),
    carrierId: nullish(ulidParam),
    freightMode: z.literal(FREIGHT_MODES, "Modalidade do frete inválida").default(9),
    volumes: nullish(z.number("Volumes inválidos").int("Volumes inválidos").min(0).max(100_000)),
    grossWeightG: nullish(z.number("Peso inválido").int("Peso inválido").min(0).max(1_000_000_000)),
    notes: text(5000),
    internalNotes: text(5000),
  })
  .superRefine((v, ctx) => {
    if (v.expectedDate && isIsoDate(v.expectedDate) && isIsoDate(v.date) && v.expectedDate < v.date)
      ctx.addIssue({
        code: "custom",
        path: ["expectedDate"],
        message: "A data prevista deve ser igual ou posterior à data do pedido",
      });
  });
export type PurchaseOrderInput = z.output<typeof purchaseOrderInput>;
export type PurchaseOrderFormValues = z.input<typeof purchaseOrderInput>;

export const purchaseOrderListQuery = listQuery.extend({
  statusId: ulidParam.optional(),
  supplierId: ulidParam.optional(),
  from: businessDate.optional(),
  to: businessDate.optional(),
});
export type PurchaseOrderListQuery = z.output<typeof purchaseOrderListQuery>;

/** Mudança de situação: a situação que a tela mostrava é obrigatória (409 `status_changed`). */
export const purchaseOrderStatusInput = z.object({ toStatusId: ulidParam, expectedFromStatusId: ulidParam });
export type PurchaseOrderStatusInput = z.output<typeof purchaseOrderStatusInput>;

/** Recebimento (parcial ou total): quantidades por item até o saldo; estoque e contas podem ser desligados. */
export const purchaseReceiveInput = z.object({
  date: businessDate.optional(),
  warehouseId: nullish(ulidParam),
  items: z
    .array(
      z.object({
        itemId: ulidParam,
        qtyQ4: z
          .number("Informe a quantidade")
          .int("Quantidade inválida")
          .min(0, "Quantidade inválida")
          .max(MAX_STOCK_QTY_Q4, "Quantidade acima do limite"),
        /** Lotes recebidos (T3.2): soma até a quantidade; o resto entra sem lote. */
        lots: z
          .array(lotQtyInput)
          .max(50, "No máximo 50 lotes por item")
          .nullish()
          .transform((v) => (v && v.length ? v : null)),
      }),
      "Informe os itens",
    )
    .min(1, "Informe a quantidade recebida de ao menos um item")
    .max(MAX_PURCHASE_ORDER_ITEMS, `No máximo ${MAX_PURCHASE_ORDER_ITEMS} itens`),
  postStock: z.boolean().default(true),
  postPayables: z.boolean().default(true),
  notes: text(1000),
});
export type PurchaseReceiveInput = z.output<typeof purchaseReceiveInput>;

export const purchaseReceiptReverseInput = z.object({ reason: text(500) });
export type PurchaseReceiptReverseInput = z.output<typeof purchaseReceiptReverseInput>;

export type PurchaseOrderStatusRef = { id: string; label: string; color: StatusTone; baseState: BaseState };

export type PurchaseOrderItemView = {
  id: string;
  position: number;
  productId: string;
  description: string;
  code: string | null;
  supplierCode: string | null;
  unit: string | null;
  qtyQ4: number;
  receivedQ4: number;
  canceledQ4: number;
  remainingQ4: number;
  unitPriceCents: number;
  ipiPct4: number;
  productsCents: number;
  ipiCents: number;
  totalCents: number;
  /** Custo rateado do item inteiro (base do custo de entrada). */
  landedCents: number;
  /** Produto com controle de lotes (T3.2): o "Receber" pede os lotes. Só na visualização do pedido. */
  batchControl?: boolean;
};

export type PurchaseOrderCalc = {
  items: Omit<PurchaseOrderItemView, "id" | "receivedQ4" | "canceledQ4" | "remainingQ4">[];
  productsTotalCents: number;
  ipiTotalCents: number;
  icmsStCents: number;
  discountPct4: number | null;
  discountCents: number;
  freightCents: number;
  otherExpensesCents: number;
  totalCents: number;
  itemsCount: number;
  quantitySumQ4: number;
  /** Parcelas previstas (a partir da data prevista, ou da data do pedido). */
  installments: InstallmentView[];
};

export type PurchaseReceiptView = {
  id: string;
  number: number;
  date: string;
  source: "manual" | "status" | "inbound_dfe";
  inboundDfe: { id: string; number: number | null; series: number | null } | null;
  warehouse: { id: string; name: string } | null;
  valueCents: number;
  stockPosted: boolean;
  payablesPosted: boolean;
  movements: number;
  titles: number;
  notes: string | null;
  items: {
    itemId: string;
    position: number;
    description: string;
    qtyQ4: number;
    valueCents: number;
    lots: { code: string; qtyQ4: number; expiresAt: string | null }[];
  }[];
  createdBy: string | null;
  createdAt: string;
  reversedAt: string | null;
  reverseReason: string | null;
};

type Ref = { id: string; name: string } | null;

export type PurchaseOrderView = Omit<PurchaseOrderCalc, "items"> & {
  id: string;
  number: number;
  purchaseRef: string | null;
  date: string;
  expectedDate: string | null;
  supplier: { id: string; name: string; document: string | null };
  warehouse: Ref;
  paymentTerm: Ref;
  financialCategory: Ref;
  carrier: Ref;
  freightMode: FreightMode;
  volumes: number | null;
  grossWeightG: number | null;
  notes: string | null;
  internalNotes: string | null;
  items: PurchaseOrderItemView[];
  receivedCents: number;
  progress: "none" | "partial" | "complete";
  receipts: PurchaseReceiptView[];
  status: PurchaseOrderStatusRef;
  nextStatuses: PurchaseOrderStatusRef[];
  /** Motivo da trava de edição; null = pode editar. */
  lockedReason: string | null;
  /** Pode receber agora (aberto/em andamento com saldo). */
  canReceive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type PurchaseOrderCreated = { id: string; number: number };

export type PurchaseOrderListItem = {
  id: string;
  number: number;
  purchaseRef: string | null;
  date: string;
  expectedDate: string | null;
  supplierName: string;
  totalCents: number;
  receivedCents: number;
  status: PurchaseOrderStatusRef;
};

/** `totalCents` só na primeira página (null nas seguintes ou acima de 2^53). */
export type PurchaseOrderListResult = {
  data: PurchaseOrderListItem[];
  nextCursor: string | null;
  summary: { count: number; totalCents: number | null };
};

export type PurchaseOrderFormOptions = {
  paymentTerms: { id: string; name: string }[];
  carriers: { id: string; name: string }[];
  warehouses: { id: string; name: string; isDefault: boolean }[];
  categories: { id: string; name: string }[];
  statuses: StatusView[];
};

export type PurchaseOrderHistoryItem = {
  id: string;
  from: { id: string; label: string } | null;
  to: { id: string; label: string };
  /** Mudança feita pelo sistema (recebimento, cancelar saldo, estorno), sem transição. */
  automatic: boolean;
  userId: string | null;
  userName: string | null;
  createdAt: string;
};

export type PurchaseTransitionResult = {
  changed: boolean;
  fromStatusId: string;
  toStatusId: string;
  received: boolean;
  reversed: number;
};

/** Pedido de compra candidato para a NF-e de entrada (mesmo fornecedor, com saldo). */
export type InboundPurchaseOrderCandidate = {
  id: string;
  number: number;
  date: string;
  purchaseRef: string | null;
  totalCents: number;
  status: PurchaseOrderStatusRef;
  matchedProducts: number;
  invoiceProducts: number;
  /** Tudo o que a nota traz (já vinculado a produtos) cabe no saldo do pedido. */
  fits: boolean;
  lines: { itemId: string; position: number; description: string; qtyQ4: number; remainingQ4: number }[];
};
